import { useEffect } from "react";
import { useLocation } from "wouter";
import { buildPageSchema } from "@/lib/seoSchema";

type SeoConfig = {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  noindex?: boolean;
};

const siteUrl = "https://variablesoft.com";
const defaultImage = `${siteUrl}/logo.png`;

const defaultSeo: SeoConfig = {
  title: "Custom Software Development Company in India | Variable Soft",
  description:
    "Variable Soft builds custom CRM, ERP, mobile apps, web apps, AI automation, web scraping, dialer, IVR and business workflow software for companies in India and worldwide.",
  keywords:
    "custom software development company in India, custom software development services, CRM development, ERP software, AI automation services, web scraping, IVR, dialer software, Micro GCC India",
};

const routeSeo: Record<string, SeoConfig> = {
  "/": defaultSeo,
  "/service": {
    title: "Best Software Development Services in India | Variable Soft",
    description:
      "Explore Variable Soft services for custom software development, web apps, mobile apps, CRM, ERP, AI automation, IVR, dialer systems, SEO and support.",
    keywords:
      "best software development services, top software company India, custom web application development, CRM ERP development, AI automation",
  },
  "/services/software-development": {
    title: "Custom Software Development Services | Variable Soft",
    description:
      "Build secure custom software, business portals, dashboards, APIs, CRM and ERP platforms with Variable Soft's software development team.",
    keywords: "custom software development services, web application development, software company Jaipur, API development",
  },
  "/services/micro-gcc-india": {
    title: "Micro-GCC & Dedicated Offshore Development Teams in India | Variable Soft",
    description:
      "Start a Micro-GCC or dedicated offshore software development team in India for CRM, automation, web apps, QA, maintenance and long-term support.",
    keywords: "Micro GCC India, dedicated offshore development team, offshore software developers India, global capability center",
  },
  "/services/whatsapp-business-api": {
    title: "WhatsApp Business API Services | Variable Soft",
    description:
      "WhatsApp Business API setup, CRM integration, chatbot flows, automated campaigns and sales/support messaging workflows by Variable Soft.",
    keywords: "WhatsApp Business API services, WhatsApp CRM integration, WhatsApp chatbot, WhatsApp automation",
  },
  "/services/ai-chatbot-development": {
    title: "AI Chatbot Development Services | Variable Soft",
    description:
      "Build AI chatbots for websites, WhatsApp, CRM workflows, support desks and internal teams with RAG knowledge-base answers and analytics.",
    keywords: "AI chatbot development, website chatbot, WhatsApp AI chatbot, RAG chatbot, VSCRM AI",
  },
  "/services/whatsapp-ai-response": {
    title: "WhatsApp AI Response Automation | Variable Soft",
    description:
      "Automate WhatsApp replies, lead qualification, support FAQs, human handoff and CRM sync with practical WhatsApp AI response workflows.",
    keywords: "WhatsApp AI response, WhatsApp automation, WhatsApp chatbot, AI replies for WhatsApp",
  },
  "/services/ai-agent-automation": {
    title: "AI Agent & Workflow Automation Services | Variable Soft",
    description:
      "Create AI agents for follow-ups, ticket triage, report generation, CRM tasks, approvals and integrated business workflow automation.",
    keywords: "AI agent automation, workflow automation, AI business automation, automated follow-up system",
  },
  "/services/generative-ai-integration": {
    title: "Generative AI Integration Services | Variable Soft",
    description:
      "Add generative AI features to software, CRM and SaaS products for summaries, content drafting, semantic search, smart forms and dashboards.",
    keywords: "generative AI integration, AI API integration, AI features for SaaS, OpenAI integration services",
  },
  "/services/ai-crm-lead-scoring": {
    title: "AI CRM & Lead Scoring Services | Variable Soft",
    description:
      "Upgrade CRM systems with AI lead scoring, follow-up suggestions, WhatsApp automation, conversation summaries and sales dashboards.",
    keywords: "AI CRM, lead scoring, CRM automation, sales automation, VSCRM AI",
  },
  "/services/ai-document-processing": {
    title: "AI Document Processing Services | Variable Soft",
    description:
      "Automate invoice reading, KYC checks, resume parsing, PDF extraction, form processing and ERP or CRM data sync with AI.",
    keywords: "AI document processing, invoice OCR, resume parsing, document automation, PDF extraction",
  },
  "/services/ai-data-analytics": {
    title: "AI Data Analytics & BI Services | Variable Soft",
    description:
      "Build dashboards, forecasting, data pipelines and AI-assisted business insights for CRM, ERP, website, sales and operations data.",
    keywords: "AI data analytics, BI dashboard development, data analytics services, business intelligence India",
  },
  "/webdesign": {
    title: "Web Design Services in India | Variable Soft",
    description:
      "Responsive, SEO-ready website design services for businesses that need fast, modern and conversion-focused websites.",
    keywords: "web design services, website design company India, responsive website design, SEO ready website",
  },
  "/software-development-in-jaipur": {
    title: "Software Development Company in Jaipur | Variable Soft",
    description:
      "Variable Soft builds websites, web apps, custom software, CRM, ERP and automation systems for businesses in Jaipur and across India.",
    keywords: "software development company Jaipur, web development Jaipur, IT company Jaipur",
  },
  "/ecommerce-development-services": {
    title: "Ecommerce Development Services | Variable Soft",
    description:
      "Build ecommerce websites, product catalogs, payment flows, order systems and custom commerce platforms with Variable Soft.",
    keywords: "ecommerce development services, ecommerce website development, online store development",
  },
  "/it-companies-in-jaipur": {
    title: "SEO & Digital Growth Services | Variable Soft",
    description:
      "Improve search visibility with technical SEO, SEO-ready development, landing pages, analytics and conversion-focused digital growth support.",
    keywords: "SEO services Jaipur, technical SEO, digital growth services, SEO ready website development",
  },
  "/top-software-companies-in-jaipur": {
    title: "Mobile App Development Services | Variable Soft",
    description:
      "Build Android, iOS and cross-platform mobile apps with APIs, clean UI, payments, notifications and long-term optimization.",
    keywords: "mobile app development services, Android app development, iOS app development, Flutter React Native",
  },
  "/portfolio": {
    title: "Software Development Portfolio | Variable Soft",
    description:
      "Explore Variable Soft projects, products and software delivery experience across CRM, ERP, dialer, websites, automation and business platforms.",
  },
  "/whoweare": {
    title: "About Variable Soft | Software Development Company in Jaipur",
    description:
      "Learn about Variable Soft, a Jaipur-based software development services company delivering custom software, automation and support.",
  },
  "/getintouch": {
    title: "Contact Variable Soft | Discuss Software, AI or Micro-GCC",
    description:
      "Contact Variable Soft for custom software development, AI automation, WhatsApp API, CRM, ERP, Micro-GCC and dedicated development team discussions.",
  },
  "/career": {
    title: "Careers at Variable Soft | Software Jobs in Jaipur",
    description:
      "Explore career opportunities at Variable Soft for software development, web development, QA, design and technology roles.",
  },
  "/team": {
    title: "Variable Soft Team | Software Development Experts",
    description:
      "Meet the Variable Soft team building custom software, websites, CRM, ERP, automation and digital platforms.",
  },
  "/blog": {
    title: "Software Development, AI and CRM Blog | Variable Soft",
    description:
      "Read Variable Soft insights on software development, CRM, AI automation, websites, ecommerce, digital growth and technology trends.",
  },
  "/india-emerging-hub": {
    title: "India as an Emerging Technology Hub | Variable Soft",
    description:
      "Why India is a strong technology hub for software development, AI automation, offshore teams and Micro-GCC delivery.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Variable Soft",
    description: "Read the Variable Soft privacy policy for website visitors, clients, candidates and users.",
  },
  "/terms-of-service": {
    title: "Terms of Service | Variable Soft",
    description: "Read the terms of service for using Variable Soft website, services and related platforms.",
  },
  "/crm": {
    title: "CRM Software and Development Services | Variable Soft",
    description:
      "Explore CRM software, lead management, dashboards, tasks, communication workflows and CRM development services by Variable Soft.",
    keywords: "CRM software, CRM development, lead management CRM, sales CRM India, VSCRM",
  },
  "/products/dialer": {
    title: "Dialer Software and Cloud Calling Platform | Variable Soft",
    description:
      "Cloud dialer software for sales and support teams with calling automation, analytics, CRM sync and communication workflows.",
  },
  "/products/hosting": {
    title: "Web Hosting Services | Variable Soft",
    description: "Reliable hosting support for business websites, applications and digital platforms by Variable Soft.",
  },
  "/products/sslcertificate": {
    title: "SSL Certificate Services | Variable Soft",
    description: "SSL certificate setup and website security support for business websites and web applications.",
  },
  "/products/taskmanagement": {
    title: "Task Management Software | Variable Soft",
    description: "Task management software for team productivity, assignment tracking, project visibility and operational workflows.",
  },
  "/products/ivr": {
    title: "IVR Software and Call Routing Solutions | Variable Soft",
    description: "IVR software, call routing, business communication automation and CRM-connected calling workflows.",
  },
  "/products/erp": {
    title: "ERP Software Development Services | Variable Soft",
    description: "ERP software for billing, inventory, operations, reporting, approvals and business process management.",
  },
  "/products/digisociety": {
    title: "Digital Society Management Software | Variable Soft",
    description: "Digital society management software for member records, communication, billing and operations.",
  },
  "/crm/what-is-crm": {
    title: "What is CRM? CRM Guide for Businesses | Variable Soft",
    description:
      "Learn what CRM software is, how it helps sales, marketing and support teams, and how businesses use CRM for lead and customer management.",
    keywords: "what is CRM, CRM software guide, customer relationship management, CRM for sales teams",
  },
  "/crm/crm-software": {
    title: "CRM Software for Business Growth | Variable Soft",
    description:
      "CRM software features for lead management, follow-ups, dashboards, task tracking, communication and business growth.",
    keywords: "CRM software, CRM software for business, lead management CRM, sales CRM",
  },
  "/crm/features": {
    title: "CRM Software Features | Variable Soft",
    description:
      "Explore CRM features including contact management, lead tracking, task assignment, reports, dashboards and customer communication.",
    keywords: "CRM features, lead tracking, CRM dashboard, customer management software",
  },
  "/crm/justdial-integration": {
    title: "Justdial CRM Integration | Variable Soft",
    description:
      "Connect Justdial leads with CRM workflows for faster follow-up, lead tracking, sales assignment and reporting.",
    keywords: "Justdial CRM integration, Justdial leads CRM, lead management integration",
  },
  "/crm/sulekha": {
    title: "Sulekha CRM Integration | Variable Soft",
    description:
      "Integrate Sulekha enquiries with CRM workflows for lead capture, assignment, follow-up automation and reporting.",
    keywords: "Sulekha CRM integration, Sulekha lead management, CRM enquiry automation",
  },
  "/maintenanceandsupport": {
    title: "Software Maintenance and Support Services | Variable Soft",
    description:
      "Ongoing software maintenance, support, bug fixing, enhancements, hosting help and long-term application care.",
  },
  "/cloud-call-center": {
    title: "Cloud Call Center Software Services | Variable Soft",
    description:
      "Cloud call center setup with dialer automation, IVR, call routing, WhatsApp integration and CRM sync.",
  },
  "/ivr-system": {
    title: "IVR System Development Services | Variable Soft",
    description:
      "Build IVR systems for call routing, customer support automation, sales workflows and CRM-connected communication.",
  },
  "/admin/login": {
    title: "Admin Login | Variable Soft",
    description: "Variable Soft admin login.",
    noindex: true,
  },
  "/admin/dashboard": {
    title: "Admin Dashboard | Variable Soft",
    description: "Variable Soft admin dashboard.",
    noindex: true,
  },
};

function upsertMeta(selector: string, attrs: Record<string, string>) {
  let tag = document.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement("meta");
    document.head.appendChild(tag);
  }
  Object.entries(attrs).forEach(([key, value]) => tag?.setAttribute(key, value));
}

function upsertLink(rel: string, href: string) {
  let tag = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.rel = rel;
    document.head.appendChild(tag);
  }
  tag.href = href;
}

function upsertJsonLd(id: string, data: unknown) {
  let tag = document.getElementById(id) as HTMLScriptElement | null;
  if (!tag) {
    tag = document.createElement("script");
    tag.id = id;
    tag.type = "application/ld+json";
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
}

export default function Seo() {
  const [location] = useLocation();

  useEffect(() => {
    const pathname = location.split("?")[0] || "/";
    const config = routeSeo[pathname] ?? (pathname.startsWith("/blogs/") ? routeSeo["/blog"] : defaultSeo);
    const canonical = `${siteUrl}${pathname === "/" ? "/" : pathname}`;
    const image = config.image ?? defaultImage;

    document.title = config.title;
    upsertMeta('meta[name="description"]', { name: "description", content: config.description });
    upsertMeta('meta[name="keywords"]', { name: "keywords", content: config.keywords ?? defaultSeo.keywords ?? "" });
    upsertMeta('meta[name="robots"]', { name: "robots", content: config.noindex ? "noindex, nofollow" : "index, follow" });
    upsertLink("canonical", canonical);

    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: config.title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: config.description });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Variable Soft" });

    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:url"]', { name: "twitter:url", content: canonical });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: config.title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: config.description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
    upsertJsonLd(
      "variablesoft-jsonld",
      buildPageSchema({
        title: config.title,
        description: config.description,
        canonical,
        pathname,
        image,
      })
    );
  }, [location]);

  return null;
}
